import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  Typography,
  ClickAwayListener,
  CircularProgress,
  Chip,
  ListItemButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import VerifiedIcon from "@mui/icons-material/Verified";

import { useAddUserMutation } from "../../../Slices/usersApiSlice";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useUserSearchQuery, useInviteToCircleMutation } from "components/Slices/circleApiSlice";
import { ImageAvatar } from "components/Circle/shared/ImageAvatar";
import InviteShareButtons from "components/Circle/shared/InviteShareButtons";

const AddUser = ({ group }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // { email, token } of the invite just created — drives the share panel.
  const [lastInvite, setLastInvite] = useState(null);
  const containerRef = useRef(null);

  const debouncedQuery = useDebouncedValue(query, 300);
  const { data: results, isLoading } = useUserSearchQuery({
    email: debouncedQuery,
    circleId: group.id,
  });

  const [addUser] = useAddUserMutation();
  const [inviteToCircle] = useInviteToCircleMutation();

  const handleUserSelect = async (user) => {
    if (!user || !user.id) {
      return;
    }

    try {
      await addUser({
        userId: user.id,
        circleId: group.id,
      }).unwrap();
    } catch (error) {
      console.error("Error adding user:", error);
      // Handle error appropriately, e.g., show a notification
      return;
    }
    setQuery("");
    setIsOpen(false);
  };

  const handleInvite = async () => {
    const email = query.trim().toLowerCase();
    if (!email) return;
    try {
      const res = await inviteToCircle({ circleId: group.id, email }).unwrap();
      // Keep the dropdown open and swap it to the share panel so the admin
      // can send the link right away — the invitee may not be a user yet.
      setLastInvite({ email, token: res.inviteToken });
    } catch (error) {
      console.error("Error inviting to circle:", error);
      return;
    }
    setQuery("");
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsOpen(false);
        setLastInvite(null);
      }}
    >
      <Box ref={containerRef} sx={{ position: "relative", width: "100%" }}>
        <TextField
          fullWidth
          variant="outlined"
          type="search"
          placeholder="Search by email"
          value={query}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value.trim())}
          onFocus={() => setIsOpen(true)}
          InputProps={{
            autoCorrect: "new-password",
            startAdornment: (
              <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
            ),
            endAdornment: isLoading && <CircularProgress size={20} />,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
        {isOpen && (isLoading || results?.users || lastInvite) && (
          <Paper
            elevation={8}
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 400,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              zIndex: 1300,
            }}
          >
            {lastInvite ? (
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: "success.light", mr: 2, width: 34, height: 34 }}>
                    <EmailIcon sx={{ fontSize: "1rem" }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2">Invitation created</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      Send {lastInvite.email} their link:
                    </Typography>
                  </Box>
                </Box>
                {lastInvite.token ? (
                  <InviteShareButtons
                    token={lastInvite.token}
                    circleTitle={group.title}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    They'll see the invite when they sign up with this email.
                  </Typography>
                )}
                <Button
                  size="small"
                  onClick={() => {
                    setLastInvite(null);
                    setIsOpen(false);
                  }}
                  sx={{ mt: 1.5, textTransform: "none", fontWeight: 600, fontSize: "0.75rem", color: "text.secondary" }}
                >
                  Done
                </Button>
              </Box>
            ) : isLoading ? (
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress size={24} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Searching...
                </Typography>
              </Box>
            ) : results?.users?.length > 0 ? (
              <List sx={{ p: 0, maxHeight: "15rem" }}>
                {results?.users.map((user, index) => (
                  <ListItem key={user.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleUserSelect(user)}
                      sx={{
                        borderBottom:
                          index < results?.users.length - 1
                            ? "1px solid"
                            : "none",
                        borderColor: "divider",
                      }}
                    >
                      <ListItemAvatar>
                        <ImageAvatar
                          displayImg={user.displayImg}
                          firstName={user.firstName}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Typography variant="subtitle2">
                              {user.firstName} {user.lastName}
                            </Typography>
                            {user.status === "active" && (
                              <VerifiedIcon sx={{ fontSize: "0.95rem", color: "primary.main" }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : query.trim() && results?.canInvite ? (
              <List sx={{ p: 0 }}>
                <ListItem
                  sx={{
                    flexDirection: "column",
                    alignItems: "stretch",
                    p: 3,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Avatar sx={{ bgcolor: "primary.light", mr: 2 }}>
                      <EmailIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2">
                        No users found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Would you like to invite "{query}"?
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={handleInvite}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    Send Invitation
                  </Button>
                </ListItem>
              </List>
            ) : query.trim() ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No users found for "{query}"
                </Typography>
              </Box>
            ) : null}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default AddUser;
